/**
 * Query-key factories — the dashboard's public contract with
 * react-query.
 *
 * Every hook that uses `useQuery` reads its key from one of these
 * factories; every mutation that invalidates reads the same factory
 * to build its invalidation target. Centralising keeps Stage 5 from
 * accidentally typo'ing a key and causing a phantom cache miss.
 *
 * Convention (matches TanStack's own `QueryKeyFactory` shape):
 *   - Level 0: resource name ("workspace", "audits", …).
 *   - Level 1: scope ("list", "detail", "bySite", …).
 *   - Level 2+: inputs (filter object, uuid, …).
 *
 * The `all` entry at each level is used for broad invalidation:
 *   `qc.invalidateQueries({ queryKey: auditKeys.all })` refreshes
 *   every audit-related query (list + every detail).
 *
 * Keys MUST be JSON-serialisable (no classes, no Dates).
 *
 * File name is `query-keys.ts` (not `keys.ts`) to avoid colliding
 * with the `keys.ts` hook file for API-keys. Don't rename.
 */

export const workspaceKeys = {
  all: ['workspace'] as const,
  /**
   * Scoped by Clerk org id so a cross-org navigation (org switch,
   * token rotation, or a stale tab resuming with a different claim)
   * invalidates cleanly. Pre-fix this was `['workspace', 'current']`
   * — unkeyed — and a manual `queryClient.clear()` in the sidebar
   * was the only thing stopping a stale cache from leaking between
   * orgs. Any path that changed the active org WITHOUT going
   * through that handler (Clerk's own `<OrganizationSwitcher>`,
   * deep link, checkout-drawer `setActive`) served the previous
   * org's data for up to `staleTime`, which is the exact class of
   * bug behind the plan-display inconsistency. Keying here is
   * defence-in-depth and costs nothing.
   */
  current: (orgId: string | null | undefined) => ['workspace', 'current', orgId ?? null] as const,
} as const;

export const usageKeys = {
  all: ['usage'] as const,
  // Scoped by Clerk org id so an org-switch invalidates cleanly.
  byOrg: (orgId: string | null | undefined) => ['usage', orgId ?? null] as const,
} as const;

export const auditKeys = {
  all: ['audits'] as const,
  lists: () => ['audits', 'list'] as const,
  list: (filter: { limit?: number; offset?: number } = {}) => ['audits', 'list', filter] as const,
  details: () => ['audits', 'detail'] as const,
  detail: (id: string) => ['audits', 'detail', id] as const,
  report: (id: string) => ['audits', 'report', id] as const,
} as const;

export const siteKeys = {
  all: ['sites'] as const,
  lists: () => ['sites', 'list'] as const,
  list: (filter: { limit?: number; offset?: number } = {}) => ['sites', 'list', filter] as const,
  details: () => ['sites', 'detail'] as const,
  detail: (uuid: string) => ['sites', 'detail', uuid] as const,
  overview: (uuid: string, days?: number) => ['sites', 'overview', uuid, days ?? null] as const,
  latest: (uuid: string, filter: { page_uuid?: string; device_profile_label?: string } = {}) =>
    ['sites', 'latest', uuid, filter] as const,
  p75: (
    uuid: string,
    filter: {
      days?: number;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['sites', 'p75', uuid, filter] as const,
  trend: (
    uuid: string,
    filter: {
      interval?: 'hour' | 'day';
      range?: number;
      page_uuid?: string;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['sites', 'trend', uuid, filter] as const,
  pages: (uuid: string) => ['sites', 'pages', uuid] as const,
  /**
   * Pages list with sparkline audit data attached. Keyed by the metric
   * CSV so a preference change repopulates instead of merging — the
   * `recent_audits[].metrics` keys differ across selections.
   */
  pagesWithAudits: (uuid: string, metricsCsv: string) =>
    ['sites', 'pages-with-audits', uuid, metricsCsv] as const,
} as const;

export const pageKeys = {
  all: ['pages'] as const,
  detail: (uuid: string) => ['pages', 'detail', uuid] as const,
  p75: (
    uuid: string,
    filter: {
      days?: number;
      device_type?: string;
      device_profile_label?: string;
      region?: string;
    } = {},
  ) => ['pages', 'p75', uuid, filter] as const,
  samples: (
    uuid: string,
    filter: {
      days?: number;
      region?: string;
      device_type?: string;
      device_profile_label?: string;
    } = {},
  ) => ['pages', 'samples', uuid, filter] as const,
} as const;

export const keyKeys = {
  all: ['api-keys'] as const,
  list: () => ['api-keys', 'list'] as const,
} as const;

export const memberKeys = {
  all: ['members'] as const,
  list: (orgUuid: string) => ['members', 'list', orgUuid] as const,
} as const;

export const teamKeys = {
  all: ['teams'] as const,
  members: (teamUuid: string) => ['teams', teamUuid, 'members'] as const,
} as const;

export const billingKeys = {
  all: ['billing'] as const,
  current: () => ['billing', 'current'] as const,
  plans: () => ['billing', 'plans'] as const,
  // Org-keyed (cross-org cache-leak defence, same as workspaceKeys.current).
  budget: (orgId: string | null | undefined) => ['billing', 'budget', orgId ?? null] as const,
  ledger: (orgId: string | null | undefined, before: number | null) =>
    ['billing', 'ledger', orgId ?? null, before] as const,
} as const;

export const webhookKeys = {
  all: ['webhooks'] as const,
  list: () => ['webhooks', 'list'] as const,
  detail: (uuid: string) => ['webhooks', 'detail', uuid] as const,
  messages: (
    filter: {
      endpoint_uuid?: string;
      event_type?: string;
      status?: string;
      limit?: number;
    } = {},
  ) => ['webhooks', 'messages', filter] as const,
  attempts: (messageUuid: string) => ['webhooks', 'attempts', messageUuid] as const,
} as const;

export const integrationKeys = {
  all: ['integrations'] as const,
  list: () => ['integrations', 'list'] as const,
  github: (orgId: string | null) => ['integrations', 'github', orgId ?? null] as const,
  google: (orgId: string | null) => ['integrations', 'google', orgId ?? null] as const,
} as const;

export const searchConsoleKeys = {
  all: ['search-console'] as const,
  trend: (siteUuid: string | null, days: number, pageUrl?: string) =>
    ['search-console', 'trend', siteUuid ?? null, days, pageUrl ?? null] as const,
  topQueries: (siteUuid: string | null, days: number) =>
    ['search-console', 'top-queries', siteUuid ?? null, days] as const,
  topPages: (siteUuid: string | null, days: number) =>
    ['search-console', 'top-pages', siteUuid ?? null, days] as const,
} as const;

export const seoKeys = {
  all: ['seo'] as const,
  sitemapHealth: (siteUuid: string | null) => ['seo', 'sitemap-health', siteUuid ?? null] as const,
} as const;

export const searchKeys = {
  all: ['search'] as const,
  query: (q: string) => ['search', q] as const,
} as const;

/**
 * Pixel-ingest read keys. Scoped by `orgId` per the cross-org-cache-leak
 * defence in plans/1.0.0/pixel-ingest/08-data-loading.md §"Query-key
 * factories". `orgId` is the Clerk org id; pass `null` when no active
 * org so the keys don't collide across the implicit "no-org" state.
 */
export const pixelKeys = {
  all: ['pixel'] as const,
  bundle: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'bundle', orgId ?? null, siteUuid, opts] as const,
  kpis: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'kpis', orgId ?? null, siteUuid, opts] as const,
  kpisTrend: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'kpis-trend', orgId ?? null, siteUuid, opts] as const,
  trend: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'trend', orgId ?? null, siteUuid, opts] as const,
  perf: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'perf', orgId ?? null, siteUuid, opts] as const,
  perfTrend: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'perf-trend', orgId ?? null, siteUuid, opts] as const,
  perfByCountry: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'perf-by-country', orgId ?? null, siteUuid, opts] as const,
  perfByPage: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'perf-by-page', orgId ?? null, siteUuid, opts] as const,
  profiles: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'profiles', orgId ?? null, siteUuid, opts] as const,
  worstSessions: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'worst-sessions', orgId ?? null, siteUuid, opts] as const,
  pagePerf: (orgId: string | null, pageUuid: string, opts: object) =>
    ['pixel', 'page-perf', orgId ?? null, pageUuid, opts] as const,
  pagePerfTrend: (orgId: string | null, pageUuid: string, opts: object) =>
    ['pixel', 'page-perf-trend', orgId ?? null, pageUuid, opts] as const,
  breakdown: (orgId: string | null, siteUuid: string, dimension: string, opts: object) =>
    ['pixel', 'breakdown', orgId ?? null, siteUuid, dimension, opts] as const,
  topPages: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'top-pages', orgId ?? null, siteUuid, opts] as const,
  // Page-focused traffic surfaces — keyed by raw page_path (not a pg-*
  // entity uuid; RUM paths aren't guaranteed to have one).
  pageTraffic: (orgId: string | null, siteUuid: string, path: string, opts: object) =>
    ['pixel', 'page-traffic', orgId ?? null, siteUuid, path, opts] as const,
  pageTrafficTrend: (orgId: string | null, siteUuid: string, path: string, opts: object) =>
    ['pixel', 'page-traffic-trend', orgId ?? null, siteUuid, path, opts] as const,
  pageTrafficBreakdown: (
    orgId: string | null,
    siteUuid: string,
    path: string,
    dimension: string,
    opts: object,
  ) => ['pixel', 'page-traffic-breakdown', orgId ?? null, siteUuid, path, dimension, opts] as const,
  pageSessions: (orgId: string | null, siteUuid: string, path: string, opts: object) =>
    ['pixel', 'page-sessions', orgId ?? null, siteUuid, path, opts] as const,
  sessionEvents: (orgId: string | null, siteUuid: string, sessionId: string, opts: object) =>
    ['pixel', 'session-events', orgId ?? null, siteUuid, sessionId, opts] as const,
  topEvents: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'top-events', orgId ?? null, siteUuid, opts] as const,
  pageTopEvents: (orgId: string | null, pageUuid: string, opts: object) =>
    ['pixel', 'page-top-events', orgId ?? null, pageUuid, opts] as const,
  attributionTop: (orgId: string | null, siteUuid: string, opts: object) =>
    ['pixel', 'attribution-top', orgId ?? null, siteUuid, opts] as const,
  live: (orgId: string | null, siteUuid: string) =>
    ['pixel', 'live', orgId ?? null, siteUuid] as const,
  health: (orgId: string | null, siteUuid: string) =>
    ['pixel', 'health', orgId ?? null, siteUuid] as const,
} as const;

/** CrUX (Chrome UX Report) field-data keys. Org-scoped like pixelKeys. */
export const cruxKeys = {
  all: ['crux'] as const,
  site: (orgId: string | null, siteUuid: string, opts: object) =>
    ['crux', 'site', orgId ?? null, siteUuid, opts] as const,
  page: (orgId: string | null, pageUuid: string, opts: object) =>
    ['crux', 'page', orgId ?? null, pageUuid, opts] as const,
  pages: (orgId: string | null, siteUuid: string) =>
    ['crux', 'pages', orgId ?? null, siteUuid] as const,
  // BigQuery-backed monthly surface (spec 02-bigquery.md). `geo` takes
  // no inputs (mirrors `pages`); `history` carries the months window.
  geo: (orgId: string | null, siteUuid: string) =>
    ['crux', 'geo', orgId ?? null, siteUuid] as const,
  history: (orgId: string | null, siteUuid: string, opts: object) =>
    ['crux', 'history', orgId ?? null, siteUuid, opts] as const,
} as const;

/** Third-party JS execution ranking — site-scoped, lab/audit-derived. */
export const thirdPartyKeys = {
  all: ['third-parties'] as const,
  site: (orgId: string | null, siteUuid: string, opts: object) =>
    ['third-parties', 'site', orgId ?? null, siteUuid, opts] as const,
} as const;

/** Deploy-markers read keys (site-scoped + per-marker delta). */
export const deployKeys = {
  all: ['deploys'] as const,
  list: (siteUuid: string, days?: number) => ['deploys', 'list', siteUuid, days ?? null] as const,
  config: (siteUuid: string) => ['deploys', 'config', siteUuid] as const,
  auditLog: (siteUuid: string) => ['deploys', 'audit-log', siteUuid] as const,
  delta: (markerUuid: string) => ['deploys', 'delta', markerUuid] as const,
} as const;

/** Variants read keys — site-scoped list, per-variant detail + results. */
export const variantKeys = {
  all: ['variants'] as const,
  bySite: (siteUuid: string) => ['variants', 'bySite', siteUuid] as const,
  detail: (uuid: string) => ['variants', 'detail', uuid] as const,
  results: (uuid: string) => ['variants', 'results', uuid] as const,
  runs: (uuid: string) => ['variants', 'runs', uuid] as const,
  catalog: ['variants', 'partner-catalog'] as const,
} as const;

/** Traffic-profile read keys — the site-scoped list is the only read. */
export const trafficProfileKeys = {
  all: ['traffic-profiles'] as const,
  bySite: (siteUuid: string) => ['traffic-profiles', 'bySite', siteUuid] as const,
} as const;

/**
 * Current-user context (whoami). Scoped by active org because
 * `is_system_admin` flips with the active org (true only when it's the
 * designated ADMIN_ORG_ID).
 */
export const userContextKeys = {
  all: ['user-context'] as const,
  current: (orgId: string | null | undefined) =>
    ['user-context', 'current', orgId ?? null] as const,
} as const;

/** Staff super-admin reads — org list (by search) + per-org reconciliation. */
export const adminKeys = {
  all: ['admin'] as const,
  orgs: (search: string) => ['admin', 'orgs', search] as const,
  org: (uuid: string) => ['admin', 'org', uuid] as const,
  sites: (orgUuid: string) => ['admin', 'sites', orgUuid] as const,
  invitations: (orgUuid: string) => ['admin', 'invitations', orgUuid] as const,
  analysisFindings: (status: string, kind: string) =>
    ['admin', 'analysis-findings', status, kind] as const,
  analysisFinding: (uuid: string) => ['admin', 'analysis-finding', uuid] as const,
  analysisStats: ['admin', 'analysis-stats'] as const,
  analysisObservations: ['admin', 'analysis-observations'] as const,
  unitEconomics: (days: number) => ['admin', 'unit-economics', days] as const,
  finances: ['admin', 'finances'] as const,
} as const;
